import React, { useState, useEffect } from 'react';
import { 
  Plus, Copy, Eye, Edit3, Trash2, Search, 
  Layers, Clock, Building2, CheckCircle2, 
  Sparkles, Snowflake, Compass, ShoppingBag, Factory,
  ArrowUpRight, AlertCircle, X, Check, Zap, Pencil
} from 'lucide-react';
import ProposalDocumentView from './ProposalDocumentView';

const INDUSTRY_ICONS = {
  cold_storage: Snowflake,
  vna_high_density: Compass,
  fmcg_retail: ShoppingBag,
  manufacturing: Factory,
  general: Layers,
};

const INDUSTRY_COLORS = {
  cold_storage: 'bg-sky-50 text-sky-700 border-sky-200',
  vna_high_density: 'bg-purple-50 text-purple-700 border-purple-200',
  fmcg_retail: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  manufacturing: 'bg-amber-50 text-amber-700 border-amber-200',
  general: 'bg-gray-50 text-gray-700 border-gray-200',
};

export default function ProposalHub({ onOpenProposal, onNewProposal }) {
  const [proposals, setProposals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // Rename state
  const [editingProposalId, setEditingProposalId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingCustomerName, setEditingCustomerName] = useState('');
  const [isSavingRename, setIsSavingRename] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // Preview Proposal Modal
  const [previewProposal, setPreviewProposal] = useState(null);

  // New Proposal Dialog
  const [showNewModal, setShowNewModal] = useState(false);
  const [newFormData, setNewFormData] = useState({
    title: '',
    customer_name: '',
    industry: 'general',
  });
  const [isCreating, setIsCreating] = useState(false);

  // Fetch proposals & templates
  const loadProposals = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/proposals');
      const json = await res.json();
      if (json.status === 'success' && json.data) {
        const t = json.data.filter(p => p.is_template);
        const u = json.data.filter(p => !p.is_template);
        setTemplates(t);
        setProposals(u);
      }
    } catch (err) {
      console.error('Error fetching proposals:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProposals();
  }, []);

  // Handle Duplicate
  const handleDuplicate = async (id, originalTitle) => {
    try {
      const res = await fetch(`/api/proposals/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `${originalTitle} (คัดลอก)`,
        })
      });
      const data = await res.json();
      if (data.status === 'success' && data.data?.id) {
        await loadProposals();
        // Immediately open the newly duplicated proposal
        onOpenProposal(data.data.id);
      }
    } catch (err) {
      console.error('Failed to duplicate proposal:', err);
      alert('เกิดข้อผิดพลาดในการคัดลอก Proposal');
    }
  };

  // Handle Single Delete
  const handleDelete = async (id, title) => {
    if (!window.confirm(`ยืนยันการลบ Proposal "${title}"? การดำเนินการนี้ไม่สามารถย้อนกลับได้`)) return;
    try {
      const res = await fetch(`/api/proposals/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        setProposals(prev => prev.filter(p => p.id !== id));
        setSelectedIds(prev => prev.filter(x => x !== id));
      }
    } catch (err) {
      console.error('Failed to delete proposal:', err);
      alert('เกิดข้อผิดพลาดในการลบ Proposal');
    }
  };

  // Handle Rename Proposal
  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const handleStartRename = (proposal) => {
    setEditingProposalId(proposal.id);
    setEditingTitle(proposal.title || '');
    setEditingCustomerName(proposal.customer_name || '');
  };

  const handleCancelRename = () => {
    setEditingProposalId(null);
    setEditingTitle('');
    setEditingCustomerName('');
  };

  const handleSaveRename = async (id) => {
    const trimmedTitle = editingTitle.trim();
    if (!trimmedTitle) {
      alert('กรุณากรอกชื่อโครงการ');
      return;
    }
    const trimmedCustomer = editingCustomerName.trim() || 'ลูกค้าทั่วไป';

    setIsSavingRename(true);
    try {
      const res = await fetch(`/api/proposals/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: trimmedTitle,
          customer_name: trimmedCustomer
        })
      });
      const data = await res.json();
      if (data.status === 'success') {
        setProposals(prev => prev.map(p => {
          if (p.id === id) {
            return {
              ...p,
              title: trimmedTitle,
              customer_name: trimmedCustomer,
              updated_at: new Date().toISOString()
            };
          }
          return p;
        }));
        setEditingProposalId(null);
        setEditingTitle('');
        setEditingCustomerName('');
        showToast('แก้ไขชื่อโครงการสำเร็จแล้ว');
      } else {
        alert(data.message || 'ไม่สามารถแก้ไขชื่อโครงการได้');
      }
    } catch (err) {
      console.error('Failed to rename proposal:', err);
      alert('เกิดข้อผิดพลาดในการบันทึกชื่อโครงการ');
    } finally {
      setIsSavingRename(false);
    }
  };

  // Handle Toggle Single Selection
  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Handle Select All / Deselect All visible
  const handleSelectAll = () => {
    const visibleIds = filteredProposals.map(p => p.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  // Handle Batch Delete Multiple Proposals
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    if (!window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบ Proposal ที่เลือกทั้งหมด ${count} รายการ?\nการดำเนินการนี้ไม่สามารถย้อนกลับได้`)) return;

    setIsBatchDeleting(true);
    try {
      const res = await fetch('/api/proposals/batch-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds })
      });
      const data = await res.json();
      if (data.status === 'success') {
        setProposals(prev => prev.filter(p => !selectedIds.includes(p.id)));
        setSelectedIds([]);
      } else {
        alert(data.message || 'เกิดข้อผิดพลาดในการลบรายการ');
      }
    } catch (err) {
      console.error('Failed batch deleting proposals:', err);
      alert('เกิดข้อผิดพลาดในการลบ Proposal หลายรายการ');
    } finally {
      setIsBatchDeleting(false);
    }
  };

  // Handle Quick Create Proposal (1-Click without manual typing)
  const handleQuickCreate = async () => {
    setIsCreating(true);
    try {
      const now = new Date();
      const dateStr = now.toLocaleDateString('th-TH', { 
        day: 'numeric', 
        month: 'short', 
        year: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const res = await fetch('/api/proposals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `Quick Concept (${dateStr})`,
          customer_name: 'แบบร่างไอเดีย / สนทนาลูกค้า',
          industry: 'general',
          status: 'draft',
        })
      });
      const json = await res.json();
      if (json.status === 'success' && json.data?.id) {
        setShowNewModal(false);
        onOpenProposal(json.data.id);
      }
    } catch (err) {
      console.error('Failed creating quick proposal:', err);
      alert('ไม่สามารถสร้าง Quick Proposal ได้');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle Submit New Proposal
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      const now = new Date();
      const dateStr = now.toLocaleDateString('th-TH', { 
        day: 'numeric', 
        month: 'short', 
        year: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const res = await fetch('/api/proposals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newFormData.title?.trim() || `Quick Concept (${dateStr})`,
          customer_name: newFormData.customer_name?.trim() || 'แบบร่างไอเดีย / สนทนาลูกค้า',
          industry: newFormData.industry || 'general',
          status: 'draft',
        })
      });
      const json = await res.json();
      if (json.status === 'success' && json.data?.id) {
        setShowNewModal(false);
        onOpenProposal(json.data.id);
      }
    } catch (err) {
      console.error('Failed creating proposal:', err);
      alert('ไม่สามารถสร้าง Proposal ใหม่ได้');
    } finally {
      setIsCreating(false);
    }
  };

  // Filtered user proposals
  const filteredProposals = proposals.filter(p => {
    const matchText = (p.title + ' ' + p.customer_name + ' ' + (p.industry || '')).toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchText && matchStatus;
  });

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-10">
      <div className="max-w-7xl mx-auto space-y-10">
        
        {/* Hero Header Banner */}
        <div className="relative overflow-hidden bg-gray-900 rounded-3xl p-8 sm:p-10 text-white shadow-xl border border-gray-800">
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-red-600/90 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                <Sparkles size={14} />
                Solution Proposal & Case Study Gallery
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                คลังข้อเสนอโครงการและจำลองคลังสินค้า
              </h1>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                สร้างข้อเสนอโครงการระดับมืออาชีพ เลือกรุ่นรถยก Toyota จากฐานข้อมูล 190 รุ่น วาดผังคลังสินค้า และจำลอง Throughput เพื่อนำเสนอแก่ลูกค้าได้ทันที
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                onClick={handleQuickCreate}
                disabled={isCreating}
                className="inline-flex items-center justify-center bg-amber-500 hover:bg-amber-600 active:scale-95 text-white font-bold px-6 py-3.5 rounded-xl shadow-md transition-all cursor-pointer text-sm"
                title="สร้างแบบร่างทันที ไม่ต้องกรอกข้อมูล เพื่อเริ่มวาดไอเดียผังคลังสินค้าได้ทันที"
              >
                {isCreating ? 'กำลังสร้าง...' : 'สร้างด่วน 1-คลิก (Quick Proposal)'}
              </button>

              <button
                onClick={() => setShowNewModal(true)}
                className="inline-flex items-center justify-center bg-red-600 hover:bg-red-700 active:scale-95 text-white font-bold px-6 py-3.5 rounded-xl shadow-md transition-all cursor-pointer text-sm"
              >
                กำหนดรายละเอียดเอง
              </button>
            </div>
          </div>
        </div>

        {/* Section 1: Verified Case Study Templates */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                <Sparkles size={20} className="text-amber-500" />
                Case Studies & เทมเพลตตัวอย่างผังคลังสินค้า (Verified Templates)
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                เลือกคัดลอกตัวอย่างผังและรถยกที่ออกแบบไว้ล่วงหน้า นำไปปรับแต่งต่อได้ทันทีโดยไม่ต้องเริ่มวาดใหม่จากศูนย์
              </p>
            </div>
            <span className="text-xs font-semibold text-gray-400 bg-white border px-3 py-1 rounded-full">
              {templates.length} รูปแบบมาตรฐาน
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {templates.map(tmpl => {
              const IconComponent = INDUSTRY_ICONS[tmpl.industry] || Layers;
              const colorClass = INDUSTRY_COLORS[tmpl.industry] || INDUSTRY_COLORS.general;
              const summary = tmpl.proposal_summary || {};
              const forklift = tmpl.forklift_config || {};

              return (
                <div 
                  key={tmpl.id}
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all flex flex-col justify-between overflow-hidden group"
                >
                  <div className="p-5 space-y-3">
                    {/* Badge */}
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-md border ${colorClass}`}>
                        <IconComponent size={13} />
                        <span className="capitalize">{tmpl.industry.replace('_', ' ')}</span>
                      </span>
                      <span className="text-[11px] font-bold text-gray-400">Template</span>
                    </div>

                    <h3 className="font-bold text-gray-900 text-sm group-hover:text-red-600 transition-colors line-clamp-2">
                      {tmpl.title}
                    </h3>

                    <p className="text-xs text-gray-500 line-clamp-3 leading-relaxed">
                      {tmpl.description}
                    </p>

                    {/* Quick Specs */}
                    <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 text-xs border border-gray-100">
                      <div className="flex justify-between">
                        <span className="text-gray-500">รุ่นรถยกแนะนำ:</span>
                        <span className="font-bold text-gray-800">{forklift.model || 'Toyota Forklift'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">ความจุจัดเก็บ:</span>
                        <span className="font-bold text-gray-800">{(summary.total_pallet_capacity || 2400).toLocaleString()} Pallets</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Throughput:</span>
                        <span className="font-bold text-emerald-600">{summary.throughput_pallets_hr || 40} Pallets/hr</span>
                      </div>
                    </div>
                  </div>

                  {/* Template Card Footer Actions */}
                  <div className="p-4 bg-gray-50/70 border-t border-gray-100 flex items-center gap-2">
                    <button
                      onClick={() => setPreviewProposal(tmpl)}
                      className="flex-1 inline-flex items-center justify-center gap-1 bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 text-xs font-medium py-2 rounded-lg transition-colors cursor-pointer"
                    >
                      <Eye size={14} />
                      ดูตัวอย่าง
                    </button>
                    <button
                      onClick={() => handleDuplicate(tmpl.id, tmpl.title)}
                      className="flex-1 inline-flex items-center justify-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium py-2 rounded-lg shadow-sm transition-colors cursor-pointer"
                    >
                      <Copy size={14} />
                      คัดลอกใช้งาน
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 2: My Proposals History */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-5">
            <div>
              <h2 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                <Layers size={20} className="text-red-600" />
                ประวัติ Proposal ของฉัน (My Proposals History)
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                รายการข้อเสนอโครงการที่คุณสร้างไว้ สามารถเปิดจำลองผัง แก้ไขข้อมูล หรือส่งออกข้อเสนอได้ทันที
              </p>
            </div>

            {/* Search, Filters, and Batch Actions */}
            <div className="flex flex-wrap items-center gap-3">
              {selectedIds.length > 0 && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 px-3 py-1.5 rounded-xl shadow-xs animate-in fade-in">
                  <span className="text-xs font-bold text-red-800">
                    เลือก {selectedIds.length} รายการ
                  </span>
                  <button
                    onClick={handleBatchDelete}
                    disabled={isBatchDeleting}
                    className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white font-bold text-xs px-3 py-1 rounded-lg shadow-xs transition-all cursor-pointer disabled:opacity-50"
                  >
                    <Trash2 size={13} />
                    {isBatchDeleting ? 'กำลังลบ...' : `ลบที่เลือก (${selectedIds.length})`}
                  </button>
                  <button
                    onClick={() => setSelectedIds([])}
                    className="text-xs text-gray-500 hover:text-gray-700 hover:bg-red-100/50 px-2 py-1 rounded-md transition-colors cursor-pointer"
                  >
                    ยกเลิก
                  </button>
                </div>
              )}

              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="ค้นหาชื่อลูกค้า / โครงการ..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 w-52 sm:w-64"
                />
              </div>

              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="all">สถานะทั้งหมด</option>
                <option value="draft">Draft (ฉบับร่าง)</option>
                <option value="in_review">In Review (อยู่ระหว่างทบทวน)</option>
                <option value="approved">Approved (อนุมัติแล้ว)</option>
              </select>
            </div>
          </div>

          {/* Proposals Table */}
          {isLoading ? (
            <div className="py-16 text-center text-gray-400">
              <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs font-medium">กำลังโหลดข้อมูล Proposal...</p>
            </div>
          ) : filteredProposals.length === 0 ? (
            <div className="py-16 text-center text-gray-500 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
              <Layers size={48} className="mx-auto text-gray-300 mb-3" />
              <h3 className="text-sm font-bold text-gray-800">ยังไม่มีประวัติ Proposal</h3>
              <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1 mb-5">
                เริ่มต้นโดยการกดปุ่มสร้าง Proposal ใหม่ หรือเลือกคัดลอกจาก Case Studies ตัวอย่างด้านบน
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={handleQuickCreate}
                  disabled={isCreating}
                  className="inline-flex items-center bg-amber-500 hover:bg-amber-600 active:scale-95 text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer shadow-sm"
                >
                  {isCreating ? 'กำลังสร้าง...' : 'สร้างด่วน 1-คลิก'}
                </button>
                <button
                  onClick={() => setShowNewModal(true)}
                  className="inline-flex items-center bg-red-600 hover:bg-red-700 active:scale-95 text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer"
                >
                  กำหนดข้อมูลลูกค้า
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b text-gray-500 font-semibold uppercase tracking-wider bg-gray-50/60">
                    <th className="py-3 px-3.5 w-10 text-center rounded-l-lg">
                      <input
                        type="checkbox"
                        checked={filteredProposals.length > 0 && filteredProposals.every(p => selectedIds.includes(p.id))}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                        title="เลือกทั้งหมด"
                      />
                    </th>
                    <th className="py-3 px-4">ชื่อโครงการ / ลูกค้า</th>
                    <th className="py-3 px-4">อุตสาหกรรม</th>
                    <th className="py-3 px-4">รุ่นรถยก</th>
                    <th className="py-3 px-4">ความจุจัดเก็บ</th>
                    <th className="py-3 px-4">สถานะ</th>
                    <th className="py-3 px-4">แก้ไขล่าสุด</th>
                    <th className="py-3 px-4 text-right rounded-r-lg">การจัดการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProposals.map(p => {
                    const forklift = p.forklift_config || {};
                    const summary = p.proposal_summary || {};
                    const isSelected = selectedIds.includes(p.id);
                    const isEditing = editingProposalId === p.id;
                    return (
                      <tr key={p.id} className={`transition-colors ${isSelected ? 'bg-red-50/70' : isEditing ? 'bg-amber-50/50' : 'hover:bg-red-50/30'}`}>
                        <td className="py-3.5 px-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(p.id)}
                            className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                          />
                        </td>
                        <td className="py-3.5 px-4">
                          {isEditing ? (
                            <div className="flex flex-col gap-1.5 min-w-[260px] max-w-sm" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] font-bold text-gray-700">แก้ไขชื่อโครงการ</span>
                                <span className="text-[10px] text-gray-400">Enter บันทึก / Esc ยกเลิก</span>
                              </div>
                              <input
                                type="text"
                                value={editingTitle}
                                onChange={e => setEditingTitle(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSaveRename(p.id);
                                  if (e.key === 'Escape') handleCancelRename();
                                }}
                                disabled={isSavingRename}
                                autoFocus
                                className="w-full text-xs font-bold px-2.5 py-1.5 border-2 border-red-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 bg-white shadow-sm"
                                placeholder="ระบุชื่อโครงการ..."
                              />
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <div className="relative flex-1">
                                  <Building2 size={12} className="absolute left-2.5 top-2 text-gray-400" />
                                  <input
                                    type="text"
                                    value={editingCustomerName}
                                    onChange={e => setEditingCustomerName(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') handleSaveRename(p.id);
                                      if (e.key === 'Escape') handleCancelRename();
                                    }}
                                    disabled={isSavingRename}
                                    className="w-full text-xs pl-7 pr-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-400 bg-white"
                                    placeholder="ชื่อลูกค้า (ไม่บังคับ)..."
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleSaveRename(p.id)}
                                  disabled={isSavingRename || !editingTitle.trim()}
                                  title="บันทึกการแก้ไข (Enter)"
                                  className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-semibold transition-colors disabled:opacity-50 flex items-center gap-1 shadow cursor-pointer flex-shrink-0"
                                >
                                  <Check size={13} />
                                  <span>{isSavingRename ? 'กำลังบันทึก...' : 'บันทึก'}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={handleCancelRename}
                                  disabled={isSavingRename}
                                  title="ยกเลิก (Esc)"
                                  className="p-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition-colors cursor-pointer flex-shrink-0"
                                >
                                  <X size={15} />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="group">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="font-bold text-gray-900 text-sm hover:text-red-600 cursor-pointer transition-colors"
                                  onClick={() => onOpenProposal(p.id)}
                                  title="คลิกเพื่อเปิดใน Simulator"
                                >
                                  {p.title}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartRename(p);
                                  }}
                                  title="แก้ไขชื่อโครงการ"
                                  className="text-gray-300 hover:text-red-600 hover:bg-red-50 p-1 rounded transition-colors cursor-pointer"
                                >
                                  <Pencil size={12} />
                                </button>
                              </div>
                              <div className="text-gray-500 text-xs flex items-center gap-1 mt-0.5">
                                <Building2 size={12} className="text-gray-400" />
                                <span>{p.customer_name || 'ลูกค้าทั่วไป'}</span>
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="py-3.5 px-4">
                          <span className="capitalize px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">
                            {(p.industry || 'general').replace('_', ' ')}
                          </span>
                        </td>

                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-gray-800">{forklift.model || 'Toyota Forklift'}</div>
                          <div className="text-[11px] text-gray-400">{forklift.series || 'Standard'}</div>
                        </td>

                        <td className="py-3.5 px-4">
                          <div className="font-bold text-gray-800">
                            {(summary.total_pallet_capacity || 0) > 0 ? `${summary.total_pallet_capacity.toLocaleString()} Pallets` : '-'}
                          </div>
                        </td>

                        <td className="py-3.5 px-4">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold capitalize ${
                            p.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                            p.status === 'in_review' ? 'bg-amber-50 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {p.status || 'draft'}
                          </span>
                        </td>

                        <td className="py-3.5 px-4 text-gray-500">
                          <div className="flex items-center gap-1">
                            <Clock size={12} className="text-gray-400" />
                            {p.updated_at ? p.updated_at.slice(0, 16) : '-'}
                          </div>
                        </td>

                        <td className="py-3.5 px-4 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              title="เปิดแก้ไขใน Simulator"
                              onClick={() => onOpenProposal(p.id)}
                              className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Edit3 size={15} />
                            </button>
                            <button
                              title="เปลี่ยนชื่อโครงการ (Rename)"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartRename(p);
                              }}
                              className="p-1.5 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              title="ดู Proposal Document"
                              onClick={() => setPreviewProposal(p)}
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              title="ทำสำเนา (Duplicate)"
                              onClick={() => handleDuplicate(p.id, p.title)}
                              className="p-1.5 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                            >
                              <Copy size={15} />
                            </button>
                            <button
                              title="ลบ Proposal"
                              onClick={() => handleDelete(p.id, p.title)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal: Preview Proposal Document */}
      {previewProposal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative shadow-2xl">
            <button
              onClick={() => setPreviewProposal(null)}
              className="absolute right-4 top-4 z-20 bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-full transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>
            <ProposalDocumentView
              proposal={previewProposal}
              mapData={previewProposal.map_data}
              config={previewProposal.fleet_config}
              selectedForklift={previewProposal.forklift_config}
              onBack={() => setPreviewProposal(null)}
            />
          </div>
        </div>
      )}

      {/* Modal: New Proposal Dialog */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-5 shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="font-black text-gray-900 text-lg flex items-center gap-2">
                <Plus size={18} className="text-red-600" />
                สร้าง Solution Proposal ใหม่
              </h3>
              <button
                onClick={() => setShowNewModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X size={18} />
              </button>
            </div>

            {/* Quick Helper Banner inside Modal */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-center justify-between gap-3">
              <div className="text-left">
                <div className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                  <Zap size={14} className="fill-amber-600 text-amber-600" />
                  ต้องการวาดไอเดียเร็วๆ?
                </div>
                <div className="text-[11px] text-amber-700 mt-0.5">
                  ข้ามการกรอกข้อมูลแล้วเข้าสู่โหมดวาด 2D ทันที
                </div>
              </div>
              <button
                type="button"
                onClick={handleQuickCreate}
                disabled={isCreating}
                className="shrink-0 bg-amber-500 hover:bg-amber-600 active:scale-95 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all cursor-pointer"
              >
                {isCreating ? 'กำลังสร้าง...' : 'สร้างด่วนทันที'}
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  ชื่อโครงการ (Project Name) <span className="text-gray-400 font-normal">(เว้นว่างได้ ระบบจะตั้งให้อัตโนมัติ)</span>
                </label>
                <input
                  type="text"
                  placeholder="เช่น โครงการคลังสินค้าขยายสาขา บางพลี"
                  value={newFormData.title}
                  onChange={e => setNewFormData({ ...newFormData, title: e.target.value })}
                  className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  ชื่อลูกค้า (Customer Name) <span className="text-gray-400 font-normal">(เว้นว่างได้ ระบบจะตั้งให้อัตโนมัติ)</span>
                </label>
                <input
                  type="text"
                  placeholder="เช่น บริษัท เอสซีจี โลจิสติกส์ แมเนจเม้นท์ จำกัด"
                  value={newFormData.customer_name}
                  onChange={e => setNewFormData({ ...newFormData, customer_name: e.target.value })}
                  className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">ประเภทอุตสาหกรรม (Industry Archetype)</label>
                <select
                  value={newFormData.industry}
                  onChange={e => setNewFormData({ ...newFormData, industry: e.target.value })}
                  className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                >
                  <option value="general">General Warehouse & Logistics</option>
                  <option value="cold_storage">Cold Storage / คลังห้องเย็น (-25°C)</option>
                  <option value="vna_high_density">High-Density VNA คลังทางเดินแคบพิเศษ</option>
                  <option value="fmcg_retail">Retail FMCG & E-Commerce ด่วน</option>
                  <option value="manufacturing">Manufacturing / Automotive โรงงานผลิต</option>
                </select>
              </div>

              <div className="pt-3 flex items-center justify-end gap-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors shadow flex items-center gap-1.5"
                >
                  {isCreating ? 'กำลังสร้าง...' : 'สร้างและเข้าสู่แบบจำลอง →'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 bg-gray-900 text-white text-xs px-4 py-3 rounded-xl shadow-2xl border border-gray-700 animate-bounce">
          <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />
          <span className="font-semibold">{toastMessage}</span>
        </div>
      )}

    </div>
  );
}
